// src/components/events/CategorySelectorModal.tsx
// Event Category 選擇器 Modal - Grid 佈局顯示 10 個分類

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { EventCategory } from '../../../types/calendar-event';
import { eventCategoryConfig, eventUnifiedColors } from '../../../constants/event.constants';

interface CategorySelectorModalProps {
  currentCategory: EventCategory;
  onSelect: (category: EventCategory) => void;
  onClose: () => void;
}

const categories = Object.keys(eventCategoryConfig) as EventCategory[];

export const CategorySelectorModal: React.FC<CategorySelectorModalProps> = ({
  currentCategory,
  onSelect,
  onClose,
}) => {
  // Escape 鍵關閉
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const modalContent = (
    <div
      className="fixed inset-0 z-modal-nested flex items-end md:items-center md:justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white w-full md:w-[360px] rounded-t-xl md:rounded-xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Select category"
      >
        {/* Header */}
        <div className="px-4 md:px-6 pt-4 pb-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Category</h3>
        </div>

        {/* Category Grid - 2 列 */}
        <div className="p-4 md:p-6 grid grid-cols-2 gap-2">
          {categories.map((category) => {
            const config = eventCategoryConfig[category];
            const Icon = config.icon;
            const isSelected = category === currentCategory;

            return (
              <button
                key={category}
                onClick={() => onSelect(category)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                  ${isSelected
                    ? `${eventUnifiedColors} ring-2 ring-red-300`
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 active:scale-95'
                  }`}
                aria-label={`${config.label}${isSelected ? ' (selected)' : ''}`}
              >
                <Icon size={16} strokeWidth={2} />
                <span>{config.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
